import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { offlineDb, scopedKey } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { noCachedCopyError, shouldFallBackToCache } from './detail-cache.service';

export interface ListCacheResult<T> {
  data: T;
  isStale: boolean;
  asOf: number | null;
}

// Generic "last successful fetch, replayed when offline" cache for list pages (Business
// Partner / Product / Loading Site). Deliberately not filter/page-aware — it just
// remembers the single most recent successful response per key, so an offline visit shows
// "whatever you last saw" with a stale banner rather than a blank error state. Reuses the
// same masterCache Dexie table as MasterCacheService (see offline-db.ts).
@Injectable({ providedIn: 'root' })
export class ListCacheService {
  cacheThrough<T>(key: string, fetch: () => Observable<T>): Observable<ListCacheResult<T>> {
    const cacheKey = scopedKey(key);
    return fetch().pipe(
      tap((data) => {
        if (isOfflineCapableNative()) {
          offlineDb.masterCache.put({ key: cacheKey, data, fetchedAt: Date.now() });
        }
      }),
      switchMap((data) => of({ data, isStale: false, asOf: null } as ListCacheResult<T>)),
      // 2026-10-02: the saved copy only stands in when there was no answer (offline) or the server itself failed (5xx). A real answer such as
      // 401 (signed out) or 429 (too many requests) is passed on as it is, and with no saved copy the REAL error is reported, not "No cached list data".
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readCache<T>(key, err) : throwError(() => err))),
    );
  }

  private readCache<T>(key: string, original?: unknown): Observable<ListCacheResult<T>> {
    const cacheKey = scopedKey(key);
    return from(offlineDb.masterCache.get(cacheKey)).pipe(
      switchMap((entry) => {
        if (!entry) {
          return throwError(() => noCachedCopyError(key, original));
        }
        return of({ data: entry.data as T, isStale: true, asOf: entry.fetchedAt });
      }),
    );
  }
}
